import { useEffect, useRef, useState } from "react";
import { ErrorMessage, Field, Form, Formik } from "formik";
import schema from "./schema";
import "./App.css";

function App() {
  const submitPaymentForm = async (values, actions) => {
    const { full_name, zip, date } = values;
    const month = date.split("/")[0];
    const year = `20${date.split("/")[1]}`;
    const requiredFields = {
      full_name,
      zip,
      month,
      year
    };

    await window.Spreedly.tokenizeCreditCard(requiredFields);
    actions.setSubmitting(false);
  };

  return (
    <Formik
      initialValues={{ full_name: "", zip: "", date: "" }}
      onSubmit={submitPaymentForm}
      validationSchema={schema}
    >
      {(formikProps) => <FormContent {...formikProps} />}
    </Formik>
  );
}

const FormContent = ({ isSubmitting, setFieldValue, isValid, dirty }) => {
  const cardRef = useRef(null);
  const cvvRef = useRef(null);
  const [cardError, setCardError] = useState(null);
  const [validCard, setValidCard] = useState(false);
  const [validCvv, setValidCvv] = useState(false);

  const handlePaymentMethod = (token, pmData) => {
    setCardError(false);
    if (document.submitForm) document.submitForm(token);
    window.ReactNativeWebView.postMessage(token);
  };

  const initializeSpreedly = () => {
    if (window.Spreedly) {
      window.Spreedly.init(process.env.REACT_APP_SPREEDLY_ENV_KEY, {
        numberEl: "card-number",
        cvvEl: "card-cvv"
      });

      window.Spreedly.on("ready", handleReady);
      window.Spreedly.on("fieldEvent", handleFieldEvent);
      window.Spreedly.on("errors", handleErrors);
      window.Spreedly.on("paymentMethod", handlePaymentMethod);
    }
  };

  const handleReady = () => {
    const fieldStyle = `background: #F7F9FC;border: none;padding: 15px; width: 100%; border-radius: 6px;`;
    const Spreedly = window.Spreedly;
    Spreedly.setFieldType("cvv", "text");
    Spreedly.setFieldType("number", "text");
    Spreedly.setNumberFormat("prettyFormat");

    Spreedly.setStyle("number", fieldStyle);
    Spreedly.setPlaceholder("number", "0000 0000 0000 0000");
    Spreedly.setStyle("cvv", fieldStyle);
    Spreedly.setPlaceholder("cvv", "***");
  };

  const handleFieldEvent = (_name, event, _activeElement, inputData) => {
    if (event === "input" && cvvRef?.current && cardRef?.current) {
      const border = "1px solid #49C88B";
      const transparent = "1px solid transparent";
      const numberIframe = cardRef?.current?.children[0];
      const cvvIframe = cvvRef?.current?.children[0];
      if (inputData["validCvv"]) {
        cvvIframe.style.border = border;
        setValidCvv(true);
      } else {
        cvvIframe.style.border = transparent;
        setValidCvv(false);
      }

      if (inputData["validNumber"]) {
        numberIframe.style.border = border;
        setValidCard(true);
        setCardError(null);
      } else {
        numberIframe.style.border = transparent;
        setValidCard(false);
      }
    }
  };

  const handleErrors = (errors) => {
    const errorBorder = "1px solid red";
    const numberIframe = cardRef?.current?.children[0];
    const cvvIframe = cvvRef?.current?.children[0];

    errors.forEach((error) => {
      switch (error.attribute) {
        case "number":
          setCardError(error.message);
          numberIframe.style.border = errorBorder;
          break;
        case "cvv":
          cvvIframe.style.border = errorBorder;
          break;
        default:
          break;
      }
    });
  };

  useEffect(() => {
    initializeSpreedly();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDateChange = (event) => {
    const { value } = event.target;
    let formattedValue = value.replace(/[^0-9]/g, "").slice(0, 4);
    if (formattedValue.length > 2 && !formattedValue.includes("/")) {
      formattedValue = `${formattedValue.slice(0, 2)}/${formattedValue.slice(
        2
      )}`;
    }
    setFieldValue("date", formattedValue);
  };

  const handleFullNameChange = (event) => {
    const { value } = event.target;
    const formattedName = value
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");

    setFieldValue("full_name", formattedName);
  };

  return (
    <Form id="payment-form" className="spf-form">
      <fieldset className="spf-fs-cc">
        <h2>Set up my payment method</h2>
        <div className="form-group-wrapper">
          <div className="form-group">
            <label>CARDHOLDER NAME</label>
            <Field
              type="text"
              name="full_name"
              placeholder="Name on card"
              onChange={handleFullNameChange}
            />
            <ErrorMessage
              name="full_name"
              component="div"
              className="error-message"
            />
          </div>
          <div className="form-group">
            <label>CARD NUMBER</label>

            <div id="card-number" ref={cardRef}>
              {validCard && <img src="/Icon.svg" alt="" />}
            </div>
            {cardError && <div className="error-message">{cardError}</div>}
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>EXPIRATION DATE</label>
              <Field
                type="text"
                name="date"
                inputmode="numeric"
                placeholder="MM/YY"
                onChange={handleDateChange}
              />
              <ErrorMessage
                name="date"
                component="div"
                className="error-message"
              />
            </div>
            <div className="form-group" id="credit_cvv">
              <label>CVV</label>
              <div id="card-cvv" ref={cvvRef}>
                {validCvv && <img src="/Icon.svg" alt="" />}
              </div>
            </div>
          </div>
          <div className="form-group" id="billing_zip">
            <label>BILLING ZIP</label>
            <Field
              type="text"
              name="zip"
              inputmode="numeric"
              placeholder="12345"
            />
            <ErrorMessage
              name="zip"
              component="div"
              className="error-message"
            />
          </div>
        </div>
      </fieldset>

      <fieldset className="spf-field-submit">
        <button
          type="submit"
          className="button"
          id="submit-button"
          disabled={
            isSubmitting || !isValid || !dirty || !validCard || !validCvv
          }
        >
          Add Card
        </button>
      </fieldset>
    </Form>
  );
};

export default App;
